// settingsPanel.ts — the single, shared SETTINGS modal (Menu gear + in-game Pause). Tactical OLED
// redesign: a full-bleed inset card with AUDIO (sound toggle + master/music sliders) and
// ACCESSIBILITY (text-size stepper + reduced-motion / colorblind / haptics toggles), the
// double-coding microcopy, and a DONE footer. Pure UI over settings.ts + the kit; no Core/network.
import Phaser from 'phaser';
import { PALETTE, hex, FONT } from './keys';
import { getSettings, setSettings, haptic, scaledFont, TEXT_SCALE_STEPS } from './settings';
import { modal } from './ui';
import { toggle, slider, stepper, ctaButton, secondaryButton, labelText } from './ui/kit';
import { openCredits } from './creditsPanel';
import { confirmDialog } from './confirmDialog';
import { _resetStatsCache } from './stats';
import { isLandscape } from './stage';
import { attachMascot } from './mascot';
import { styleHeroText } from './juice';

/** localStorage keys that hold the player's *progress* (cleared by the Reset action; settings stay). */
const PROGRESS_KEYS = ['trivia.stats.v1', 'trivia.mastery.v1', 'trivia.cosmetic.v1', 'trivia.customlab.recents.v1'];

function resetProgress(): void {
  try {
    for (const k of PROGRESS_KEYS) window.localStorage.removeItem(k);
  } catch {
    // private mode / blocked storage — nothing persisted to clear.
  }
  _resetStatsCache();
}

function scaleLabel(v: number): string {
  if (Math.abs(v - 1) < 0.001) return '100%';
  return `${Math.round(v * 100)}%`;
}

/** Step the text-scale setting by direction (±1), clamped to the allowed steps. */
function stepTextScale(dir: number): void {
  const cur = getSettings().textScale;
  let i = TEXT_SCALE_STEPS.findIndex((s) => Math.abs(s - cur) < 0.001);
  if (i < 0) i = TEXT_SCALE_STEPS.findIndex((s) => Math.abs(s - 1) < 0.001);
  const next = Math.max(0, Math.min(TEXT_SCALE_STEPS.length - 1, i + dir));
  setSettings({ textScale: TEXT_SCALE_STEPS[next] ?? 1 });
}

export function openSettings(scene: Phaser.Scene, onClose?: () => void): ReturnType<typeof modal> {
  // S3 item 11: content-hugging — size the card to its fixed AUDIO + ACCESSIBILITY + footer stack so
  // there is no mid-panel dead band (the row pitch is constant, so this height is stable across the
  // a11y text-scale). Clamped to the viewport by modal(), so a small screen still fills as before.
  const W = scene.scale.width;
  const land = isLandscape();
  const m = modal(scene, {
    title: 'SETTINGS',
    contentHeight: land ? 684 : 644,
    onClose,
    ...(land ? { widthPx: Math.min(628, W * 0.49), centerX: W * 0.735 } : {}),
  });
  if (land) drawDesktopSettingsContext(scene, m.layer, (m.card.x ?? W / 2) - m.cardW / 2);
  const { card, cardW, bodyTop, footerY } = m;
  const rowX = -cardW / 2 + 26;
  const valX = cardW / 2 - 26;
  const rowFs = `${scaledFont(19, 13)}px`;

  const mkLabel = (str: string, color = PALETTE.text): Phaser.GameObjects.Text =>
    scene.add.text(rowX, 0, str, { fontFamily: FONT.family, fontSize: rowFs, color: hex(color) }).setOrigin(0, 0.5);

  let y = bodyTop;

  // ── AUDIO ──
  card.add(labelText(scene, rowX, y, 'AUDIO', { size: 12, color: PALETTE.scan }));
  y += 48;
  // Sound toggle binds MUTE (on = un-muted). Muting still preserves stored levels, but the slider rows
  // visibly yield so the panel does not read as "Sound OFF, audio still active."
  const sound = mkLabel('Sound');
  sound.setY(y);
  let refreshAudioMuteState = (): void => {};
  const soundToggle = toggle(scene, valX - 23, y, !getSettings().muted, (on) => {
    setSettings({ muted: !on });
    refreshAudioMuteState();
  });
  const mutedNotice = scene.add
    .text(rowX, y + 23, 'Audio is muted', {
      fontFamily: FONT.family,
      fontSize: `${scaledFont(11, 10)}px`,
      color: hex(PALETTE.mute),
      fontStyle: 'bold',
    })
    .setOrigin(0, 0.5);
  card.add([sound, soundToggle, mutedNotice]);
  y += 56;
  // Master volume slider
  const mv = mkLabel('Master volume');
  mv.setY(y);
  const mvVal = scene.add.text(valX, y, `${Math.round(getSettings().volume * 100)}%`, { fontFamily: FONT.display, fontSize: rowFs, color: hex(PALETTE.scan), fontStyle: 'bold' }).setOrigin(1, 0.5);
  card.add([mv, mvVal]);
  y += 34;
  const masterSlider = slider(scene, 0, y, cardW - 64, getSettings().volume, (f) => {
    setSettings({ volume: f });
    mvVal.setText(`${Math.round(f * 100)}%`);
  });
  card.add(masterSlider.container);
  y += 48;
  // Music slider
  const mu = mkLabel('Music');
  mu.setY(y);
  const muVal = scene.add.text(valX, y, `${Math.round(getSettings().musicVolume * 100)}%`, { fontFamily: FONT.display, fontSize: rowFs, color: hex(PALETTE.scan), fontStyle: 'bold' }).setOrigin(1, 0.5);
  card.add([mu, muVal]);
  y += 34;
  const musicSlider = slider(scene, 0, y, cardW - 64, getSettings().musicVolume, (f) => {
    setSettings({ musicVolume: f });
    muVal.setText(`${Math.round(f * 100)}%`);
  });
  card.add(musicSlider.container);
  refreshAudioMuteState = (): void => {
    const muted = getSettings().muted;
    mutedNotice.setVisible(muted);
    const alpha = muted ? 0.42 : 1;
    const sliderAlpha = muted ? 0.24 : 1;
    mv.setAlpha(alpha);
    mvVal.setAlpha(muted ? 0.5 : 1);
    masterSlider.container.setAlpha(sliderAlpha);
    mu.setAlpha(alpha);
    muVal.setAlpha(muted ? 0.5 : 1);
    musicSlider.container.setAlpha(sliderAlpha);
    (masterSlider.container.getData('setEnabled') as ((on: boolean) => void) | undefined)?.(!muted);
    (musicSlider.container.getData('setEnabled') as ((on: boolean) => void) | undefined)?.(!muted);
  };
  refreshAudioMuteState();
  y += 56;

  // ── ACCESSIBILITY ──
  card.add(labelText(scene, rowX, y, 'ACCESSIBILITY', { size: 12, color: PALETTE.scan }));
  y += 48;
  // Text size stepper
  const ts = mkLabel('Text size');
  ts.setY(y);
  card.add([ts, stepper(scene, valX - 60, y, () => scaleLabel(getSettings().textScale), () => stepTextScale(-1), () => stepTextScale(1))]);
  y += 56;
  const mkToggle = (label: string, getOn: () => boolean, onChange: (on: boolean) => void): void => {
    const l = mkLabel(label);
    l.setY(y);
    card.add([l, toggle(scene, valX - 23, y, getOn(), onChange)]);
    y += 56;
  };
  mkToggle('Reduced motion', () => getSettings().reducedMotion, (on) => setSettings({ reducedMotion: on }));
  mkToggle('Colorblind assist', () => getSettings().colorblind, (on) => setSettings({ colorblind: on }));
  mkToggle('Haptics', () => getSettings().haptics, (on) => {
    setSettings({ haptics: on });
    if (on) haptic(30);
  });

  // ── footer ──
  // microcopy (the double-coding guarantee) — S3 item 13: `dim` (not `mute`) clears WCAG-AA 4.5:1.
  card.add(
    scene.add
      .text(0, footerY - 104, 'Every color has a shape. Every sound has a cue.', {
        fontFamily: FONT.family,
        fontSize: `${scaledFont(12, 11)}px`,
        color: hex(PALETTE.dim),
        align: 'center',
        wordWrap: { width: cardW - 80 },
      })
      .setOrigin(0.5),
  );
  // S3 item 16: CREDITS (secondary) + RESET PROGRESS (danger tertiary) are now real docked buttons,
  // not bare text links — consistent shape/height, ≥44px targets, side-by-side above the DONE CTA.
  const secGap = 12;
  const secW = (cardW - 52 - secGap) / 2;
  const secY = footerY - 64;
  card.add(
    secondaryButton(scene, -secW / 2 - secGap / 2, secY, 'CREDITS', () => openCredits(scene), {
      width: secW,
      height: 44,
      icon: 'info',
      textColor: PALETTE.scan,
      fontPx: 14,
    }),
  );
  card.add(
    secondaryButton(scene, secW / 2 + secGap / 2, secY, 'RESET', () =>
      confirmDialog(scene, {
        title: 'Reset progress?',
        message: 'This clears your stats, streaks, missions, mastery, and skins on this device. This cannot be undone.',
        confirmLabel: 'Reset',
        danger: true,
        onConfirm: resetProgress,
      }), { width: secW, height: 44, icon: 'again', textColor: PALETTE.hazard, fontPx: 14 }),
  );

  // DONE
  card.add(ctaButton(scene, 0, footerY, 'Done', () => m.close(), { width: cardW - 52, height: 56 }));

  return m;
}

function drawDesktopSettingsContext(scene: Phaser.Scene, layer: Phaser.GameObjects.Container, cardLeft: number): void {
  const H = scene.scale.height;
  const panelW = Math.max(320, Math.min(500, cardLeft - 64));
  const cx = Math.max(panelW / 2 + 24, cardLeft / 2 - 10);
  const els: Phaser.GameObjects.GameObject[] = [];
  const frame = scene.add.graphics();
  frame.fillStyle(PALETTE.surface, 0.12);
  frame.fillRoundedRect(cx - panelW / 2, H * 0.12, panelW, H * 0.76, 18);
  frame.lineStyle(1.25, PALETTE.line, 0.4);
  frame.strokeRoundedRect(cx - panelW / 2, H * 0.12, panelW, H * 0.76, 18);
  els.push(frame);

  const fs = 34;
  const t1 = scene.add
    .text(0, H * 0.22, 'TRIVIA', { fontFamily: FONT.display, fontSize: `${fs}px`, color: '#ffffff', fontStyle: 'bold' })
    .setOrigin(0, 0.5);
  const t2 = scene.add
    .text(0, H * 0.22, 'MUNCHERS', { fontFamily: FONT.display, fontSize: `${fs}px`, color: '#ffffff', fontStyle: 'bold' })
    .setOrigin(0, 0.5);
  styleHeroText(t1, { top: '#f5f8ff', bottom: '#b7c1d6', tracking: 1, stroke: '#0a0b12' });
  styleHeroText(t2, { top: '#9bf1ff', bottom: '#1ec3e6', tracking: 1, stroke: '#062028' });
  const gap = 9;
  const titleW = t1.width + gap + t2.width;
  t1.setX(cx - titleW / 2);
  t2.setX(cx - titleW / 2 + t1.width + gap);
  els.push(t1, t2);
  els.push(
    scene.add
      .text(cx, H * 0.275, 'TUNE THE CABINET WITHOUT LOSING THE MATCH', {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(12, 11)}px`,
        color: hex(PALETTE.dim),
        fontStyle: '600',
        align: 'center',
      })
      .setOrigin(0.5),
  );

  const player = attachMascot(scene, 'player', cx - 58, H * 0.49, 132, { depth: 30_001, animate: true });
  const rival = attachMascot(scene, 'rival', cx + 76, H * 0.5, 108, { depth: 30_001, animate: true });
  els.push(player.obj, rival.obj);
  const glow = scene.add.graphics();
  glow.fillStyle(PALETTE.player, 0.12);
  glow.fillEllipse(cx - 22, H * 0.57, 248, 34);
  glow.lineStyle(1.2, PALETTE.scan, 0.35);
  glow.strokeEllipse(cx - 22, H * 0.57, 248, 34);
  els.push(glow);

  els.push(
    scene.add
      .text(cx, H * 0.675, 'Every color has a shape. Every sound has a cue.', {
        fontFamily: FONT.family,
        fontSize: `${scaledFont(12, 11)}px`,
        color: hex(PALETTE.text),
        align: 'center',
        wordWrap: { width: panelW - 58 },
      })
      .setOrigin(0.5),
  );
  const loader = scene.add.graphics();
  const barW = Math.min(300, panelW - 92);
  loader.fillStyle(PALETTE.tile, 1);
  loader.fillRoundedRect(cx - barW / 2, H * 0.735, barW, 8, 4);
  loader.fillStyle(PALETTE.scan, 1);
  loader.fillRoundedRect(cx - barW / 2, H * 0.735, barW * 0.7, 8, 4);
  loader.fillStyle(PALETTE.armed, 1);
  loader.fillRoundedRect(cx - barW / 2 + barW * 0.7, H * 0.735, barW * 0.12, 8, 4);
  els.push(loader);
  els.push(
    scene.add
      .text(cx, H * 0.775, 'ACCESSIBILITY PROFILE: READY', {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(12, 11)}px`,
        color: hex(PALETTE.scan),
        fontStyle: 'bold',
      })
      .setOrigin(0.5),
  );

  layer.add(els);
}
